import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { jevService, CategorySuggestion } from '../services/jev-service';

const pct = (p?: string) => (p == null ? '' : `${Math.round(parseFloat(p) * 100)}%`);

const money = (amount: string) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(parseFloat(amount));

/**
 * The "Needs review" queue: transactions Jev categorised below the household's
 * confidence threshold (issue #26). One click applies the top suggestion, or
 * the runner-up; either removes the row from the queue.
 */
export const CategorySuggestionsPanel: React.FC<{ onCountChange?: (n: number) => void }> = ({ onCountChange }) => {
  const [items, setItems] = useState<CategorySuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await jevService.listSuggestions();
      setItems(r.suggestions ?? []);
      onCountChange?.(r.total ?? 0);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? 'Could not load suggestions');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const accept = async (s: CategorySuggestion, categoryId?: string) => {
    setBusy(s.transaction_id);
    try {
      await jevService.acceptSuggestion(s.transaction_id, categoryId);
      const rest = items.filter((i) => i.transaction_id !== s.transaction_id);
      setItems(rest);
      onCountChange?.(rest.length);
    } catch (e: any) {
      setError(e?.message ?? 'Could not apply the category');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {error && <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {items.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Nothing to review.</Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Needs review">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Account</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Suggestion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.transaction_id} hover>
                  <TableCell>{s.date.slice(0, 10)}</TableCell>
                  <TableCell>{s.description}</TableCell>
                  <TableCell>{s.account_name}</TableCell>
                  <TableCell align="right">{money(s.amount)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small" variant="contained"
                        disabled={busy === s.transaction_id}
                        onClick={() => accept(s)}
                      >
                        {`${s.top1_category_name} (${pct(s.p1)})`}
                      </Button>
                      {s.top2_category_id && s.top2_category_name && (
                        <Button
                          size="small" variant="outlined"
                          disabled={busy === s.transaction_id}
                          onClick={() => accept(s, s.top2_category_id)}
                        >
                          {`${s.top2_category_name} (${pct(s.p2)})`}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};
